/**
 * 予算(budgets)の読み書き。粒度は 事業部 × 科目 × 月(decisions.md 2026-08-20)。
 * 階層別予算はフェーズ3以降。
 *
 * ドキュメントIDは `${department_id}_${fiscal_year}_${month}_${account_id}` に固定する。
 * 同じ枠を二重に登録できなくなり、貼り付けによる一括更新も上書きで済む。
 */
import {
  collection,
  doc,
  getDocs,
  query,
  serverTimestamp,
  where,
  writeBatch,
  type QueryDocumentSnapshot,
} from 'firebase/firestore';
import { getDb } from '@core/lib/firebase';
import type { Budget, DepartmentId, MonthStr, Yen } from '@core/lib/types';

const COLLECTION = 'budgets';

function withId(snap: QueryDocumentSnapshot): Budget {
  return { id: snap.id, ...(snap.data() as object) } as Budget;
}

export function budgetDocId(params: {
  departmentId: DepartmentId;
  fiscalYear: number;
  month: MonthStr;
  accountId: string;
}): string {
  return `${params.departmentId}_${params.fiscalYear}_${params.month}_${params.accountId}`;
}

/** 事業部×年度の予算をまとめて取得する(年度グリッドの表示用) */
export async function fetchBudgetsForYear(
  departmentId: DepartmentId,
  fiscalYear: number,
): Promise<Budget[]> {
  const snap = await getDocs(
    query(
      collection(getDb(), COLLECTION),
      where('department_id', '==', departmentId),
      where('fiscal_year', '==', fiscalYear),
    ),
  );
  return snap.docs.map(withId);
}

export type BudgetCell = {
  departmentId: DepartmentId;
  fiscalYear: number;
  month: MonthStr;
  accountId: string;
  amount: Yen;
};

/**
 * 予算をまとめて保存する。
 * 金額0の枠はドキュメントを作らず削除する(空欄と0を同じ意味として扱い、無駄な行を残さない)。
 */
export async function saveBudgets(cells: BudgetCell[], userId: string): Promise<number> {
  const db = getDb();
  const chunkSize = 200;
  let written = 0;

  for (let offset = 0; offset < cells.length; offset += chunkSize) {
    const batch = writeBatch(db);
    for (const cell of cells.slice(offset, offset + chunkSize)) {
      const ref = doc(db, COLLECTION, budgetDocId(cell));
      if (cell.amount === 0) {
        batch.delete(ref);
      } else {
        batch.set(
          ref,
          {
            department_id: cell.departmentId,
            fiscal_year: cell.fiscalYear,
            month: cell.month,
            account_id: cell.accountId,
            amount: cell.amount,
            updated_by: userId,
            updated_at: serverTimestamp(),
          },
          { merge: true },
        );
      }
      written++;
    }
    await batch.commit();
  }
  return written;
}
